import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '@/models/ServiceTypes';
import { Role } from '@/models/AccountEnums';
import type {
  AdvisorCusipDiversification,
  AdvisorHoldingRow,
  BulkEditCusipDiversificationRequest,
  CusipRow,
  HoldingDiversificationsResponse,
  HoldingWithoutCusipRow,
  UnclassifiedHoldingRow,
  UpdateHoldingDiversificationRequest,
} from '@/modules/dataFeeds/types';
import {
  buildCusipsQueryParams,
  type CusipsListRequest,
} from '@/modules/dataFeeds/utils/buildCusipsQueryParams';
import {
  buildAdvisorHoldingsExportBody,
  completeAdvisorHoldingsExport,
  type AdvisorHoldingsExportListArgs,
} from '@/modules/dataFeeds/utils/exportAdvisorHoldings';
import {
  buildHoldingsWithoutCusipsExportBody,
  HOLDINGS_WO_CUSIPS_EXPORT_FILENAME,
  type UserExportColumn,
} from '@/modules/dataFeeds/utils/exportHoldingsWithoutCusips';
import {
  ADVISOR_HOLDINGS_URL,
  classificationUrls,
  getClassificationScope,
  type ClassificationScope,
} from '@/modules/dataFeeds/utils/classificationEndpoints';
import { encodeCusipForUrl } from '@/modules/dataFeeds/utils/encodeCusipForUrl';
import { MimeType } from '@/utils/fileValidation';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';
import { waServicesQuery } from './baseQueries';
import type { WARootState } from './store';

export type GetCusipsArgs = CusipsListRequest & {
  advisorGroupId: number | null;
};

export type GetAdvisorHoldingsArgs = CusipsListRequest & {
  OnlyUnclassified: boolean;
};

export type ExportAdvisorHoldingsArgs = AdvisorHoldingsExportListArgs & {
  columns: UserExportColumn[];
  token: string;
};

export type ExportCusipsArgs = CusipsListRequest & {
  advisorGroupId: number | null;
};

export type GetUnclassifiedCusipsArgs = CusipsListRequest & {
  advisorGroupId: number | null;
};

export type ExportUnclassifiedCusipsArgs = CusipsListRequest & {
  advisorGroupId: number | null;
  fileName: string;
  columns: UserExportColumn[];
};

export type ExportHoldingsWithoutCusipsArgs = CusipsListRequest & {
  advisorGroupId: number;
  columns: UserExportColumn[];
};

const CUSIPS_EXPORT_FILENAME = 'AssetClassification.xls';
const UNCLASSIFIED_EXPORT_LIMIT = 5_000_000;

function firmRoleForbiddenError(
  state: WARootState,
): FetchBaseQueryError | null {
  if (state.auth.currentRole !== Role.Firm) {
    return {
      status: 403,
      data: 'Feed classifications are only available for firm role',
    };
  }
  return null;
}

function classificationScopeOrError(
  state: WARootState,
): { scope: ClassificationScope } | { error: FetchBaseQueryError } {
  const scope = getClassificationScope(state.auth.currentRole);
  if (scope == null) {
    return {
      error: {
        status: 403,
        data: 'Feed classifications are only available for firm and advisor roles',
      },
    };
  }
  return { scope };
}

function missingFirmGroupError(
  scope: ClassificationScope,
  advisorGroupId: number | null,
): FetchBaseQueryError | null {
  if (scope === Role.Firm && advisorGroupId == null) {
    return {
      status: 400,
      data: 'Advisor group id is required for firm requests',
    };
  }
  return null;
}

function resolveExportToken(
  state: WARootState,
  missingTokenMessage: string,
): { token: string } | { error: FetchBaseQueryError } {
  const token = state.appView.UserInfo?.Token;
  if (token == null || token === '') {
    return {
      error: {
        status: 401,
        data: missingTokenMessage,
      },
    };
  }
  return { token };
}

async function completeXlsBlobExport(args: {
  blob: Blob;
  filename: string;
  downloadFailureMessage: string;
}): Promise<
  { data: { success: true; filename: string } } | { error: FetchBaseQueryError }
> {
  try {
    await triggerBlobDownload(args.blob, args.filename, MimeType.Xls);
    return {
      data: { success: true, filename: args.filename },
    };
  } catch (downloadError) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: args.downloadFailureMessage,
        data: String(downloadError),
      } as FetchBaseQueryError,
    };
  }
}

function invalidExportBlobError(
  data: object | string | number | boolean | null,
): FetchBaseQueryError {
  return {
    status: 'CUSTOM_ERROR',
    error: 'Invalid data type returned, expected Blob',
    data,
  };
}

function cusipDiversificationsTagId(
  advisorGroupId: number | null,
  cusip: string,
): string {
  // The advisor detail query carries no group id, so its tag is group-independent.
  if (advisorGroupId == null) {
    return advisorCusipDiversificationsTagId(cusip);
  }
  return `${String(advisorGroupId)}:${cusip}`;
}

function advisorCusipDiversificationsTagId(cusip: string): string {
  return `advisor:${cusip}`;
}

type CusipSaveTagArgs = {
  advisorGroupId: number;
  cusip: string;
  scope?: ClassificationScope;
};

function cusipSaveTags(args: CusipSaveTagArgs) {
  const id = String(args.advisorGroupId);
  const advisor = args.scope === Role.Advisor;
  return [
    // The advisor list is cached without a group id, so it is invalidated untyped.
    advisor ? { type: 'Cusips' as const } : { type: 'Cusips' as const, id },
    advisor
      ? { type: 'UnclassifiedCusips' as const }
      : { type: 'UnclassifiedCusips' as const, id },
    { type: 'HoldingsWOCusips' as const, id },
    { type: 'AdvisorHoldings' as const },
    {
      type: 'CusipDiversifications' as const,
      id: advisor
        ? advisorCusipDiversificationsTagId(args.cusip)
        : cusipDiversificationsTagId(args.advisorGroupId, args.cusip),
    },
  ];
}

function advisorGroupUrl(advisorGroupId: number, path: string): string {
  return `/api/firm/advisorGroups/${String(advisorGroupId)}${path}`;
}

/**
 * Builds the relative DataUrl for POST /api/user/Export (no leading slash).
 * The export service rejects DataUrl values that do not start with `api/`.
 */
function buildUnclassifiedExportDataUrl(
  scope: ClassificationScope,
  advisorGroupId: number | null,
  listArgs: CusipsListRequest,
): string {
  const params = {
    ...buildCusipsQueryParams({
      ...listArgs,
      Page: 1,
      Limit: UNCLASSIFIED_EXPORT_LIMIT,
    }),
    IsExport: true,
  };
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    search.set(key, String(value));
  }
  // The export service needs the DataUrl without the leading slash.
  return `${classificationUrls.unclassifiedCusips(scope, advisorGroupId).slice(1)}?${search.toString()}`;
}

export const feedClassificationsApi = createApi({
  reducerPath: 'feedClassificationsApi',
  baseQuery: waServicesQuery,
  tagTypes: [
    'Cusips',
    'UnclassifiedCusips',
    'HoldingsWOCusips',
    'CusipDiversifications',
    'HoldingDiversifications',
    'AdvisorHoldings',
  ],
  endpoints: (builder) => ({
    getAdvisorHoldings: builder.query<
      RecordResponse<AdvisorHoldingRow>,
      GetAdvisorHoldingsArgs
    >({
      query: ({ OnlyUnclassified, ...listArgs }) => ({
        url: ADVISOR_HOLDINGS_URL,
        method: 'GET',
        params: { ...buildCusipsQueryParams(listArgs), OnlyUnclassified },
      }),
      providesTags: [{ type: 'AdvisorHoldings' }],
    }),
    exportAdvisorHoldings: builder.mutation<
      { success: true; filename: string },
      ExportAdvisorHoldingsArgs
    >({
      query: ({ columns, token, ...listArgs }) => ({
        url: '/api/user/Export',
        method: 'POST',
        body: buildAdvisorHoldingsExportBody({ listArgs, columns, token }),
        responseHandler: completeAdvisorHoldingsExport,
      }),
    }),
    getCusips: builder.query<RecordResponse<CusipRow>, GetCusipsArgs>({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const { advisorGroupId, ...listArgs } = args;
        const groupError = missingFirmGroupError(scope, advisorGroupId);
        if (groupError != null) {
          return { error: groupError };
        }
        const result = await baseQuery({
          url: classificationUrls.cusips(scope, advisorGroupId),
          method: 'GET',
          params: buildCusipsQueryParams(listArgs),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<CusipRow> };
      },
      providesTags: (_result, _error, { advisorGroupId }) => [
        { type: 'Cusips', id: String(advisorGroupId) },
      ],
    }),
    exportCusips: builder.mutation<
      { success: true; filename: string },
      ExportCusipsArgs
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const tokenResult = resolveExportToken(
          state,
          'Missing auth token for Cusips export',
        );
        if ('error' in tokenResult) {
          return { error: tokenResult.error };
        }

        const { advisorGroupId, ...listArgs } = args;
        const groupError = missingFirmGroupError(scope, advisorGroupId);
        if (groupError != null) {
          return { error: groupError };
        }
        const result = await baseQuery({
          url: classificationUrls.cusipsExport(scope, advisorGroupId),
          method: 'GET',
          params: {
            ...buildCusipsQueryParams(listArgs),
            token: tokenResult.token,
          },
          responseHandler: async (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        if (!(result.data instanceof Blob)) {
          return { error: invalidExportBlobError(result.data ?? null) };
        }

        return completeXlsBlobExport({
          blob: result.data,
          filename: CUSIPS_EXPORT_FILENAME,
          downloadFailureMessage: 'Failed to trigger Cusips export download',
        });
      },
    }),
    exportHoldingsWithoutCusips: builder.mutation<
      { success: true; filename: string },
      ExportHoldingsWithoutCusipsArgs
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = firmRoleForbiddenError(state);
        if (roleError != null) {
          return { error: roleError };
        }

        const tokenResult = resolveExportToken(
          state,
          'Missing auth token for holdings without CUSIPs export',
        );
        if ('error' in tokenResult) {
          return { error: tokenResult.error };
        }

        const { advisorGroupId, columns, ...listArgs } = args;
        const body = buildHoldingsWithoutCusipsExportBody({
          advisorGroupId,
          listArgs,
          columns,
          token: tokenResult.token,
          currentRole: Role.Firm,
        });

        const result = await baseQuery({
          url: '/api/user/Export',
          method: 'POST',
          body,
          responseHandler: async (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        if (!(result.data instanceof Blob)) {
          return { error: invalidExportBlobError(result.data ?? null) };
        }

        return completeXlsBlobExport({
          blob: result.data,
          filename: HOLDINGS_WO_CUSIPS_EXPORT_FILENAME,
          downloadFailureMessage:
            'Failed to trigger holdings without CUSIPs export download',
        });
      },
    }),
    getUnclassifiedCusips: builder.query<
      RecordResponse<UnclassifiedHoldingRow>,
      GetUnclassifiedCusipsArgs
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const { advisorGroupId, ...listArgs } = args;
        const groupError = missingFirmGroupError(scope, advisorGroupId);
        if (groupError != null) {
          return { error: groupError };
        }
        const result = await baseQuery({
          url: classificationUrls.unclassifiedCusips(scope, advisorGroupId),
          method: 'GET',
          params: buildCusipsQueryParams(listArgs),
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: result.data as RecordResponse<UnclassifiedHoldingRow>,
        };
      },
      providesTags: (_result, _error, { advisorGroupId }) => [
        { type: 'UnclassifiedCusips', id: String(advisorGroupId) },
      ],
    }),
    exportUnclassifiedCusips: builder.mutation<
      { success: true; filename: string },
      ExportUnclassifiedCusipsArgs
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const token = state.appView.UserInfo?.Token;
        if (token == null || token === '') {
          return {
            error: {
              status: 401,
              data: 'Missing auth token for Unclassified export',
            } as FetchBaseQueryError,
          };
        }

        const { advisorGroupId, fileName, columns, ...listArgs } = args;
        const groupError = missingFirmGroupError(scope, advisorGroupId);
        if (groupError != null) {
          return { error: groupError };
        }
        const downloadFileName = `${fileName}.xls`;
        const result = await baseQuery({
          url: '/api/user/Export',
          method: 'POST',
          body: {
            DataUrl: buildUnclassifiedExportDataUrl(
              scope,
              advisorGroupId,
              listArgs,
            ),
            Token: token,
            CurrentRole: scope,
            FileName: fileName,
            Columns: columns,
          },
          responseHandler: async (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        if (!(result.data instanceof Blob)) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Invalid data type returned, expected Blob',
              data: result.data,
            } as FetchBaseQueryError,
          };
        }

        try {
          await triggerBlobDownload(
            result.data,
            downloadFileName,
            MimeType.Xls,
          );
          return {
            data: { success: true, filename: downloadFileName },
          };
        } catch (downloadError) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Failed to trigger Unclassified export download',
              data: String(downloadError),
            } as FetchBaseQueryError,
          };
        }
      },
    }),
    getHoldingsWithoutCusips: builder.query<
      RecordResponse<HoldingWithoutCusipRow>,
      GetCusipsArgs & { advisorGroupId: number }
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = firmRoleForbiddenError(state);
        if (roleError != null) {
          return { error: roleError };
        }

        const { advisorGroupId, ...listArgs } = args;
        const result = await baseQuery({
          url: advisorGroupUrl(advisorGroupId, '/holdingsWOCusips'),
          method: 'GET',
          params: buildCusipsQueryParams(listArgs),
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: result.data as RecordResponse<HoldingWithoutCusipRow>,
        };
      },
      providesTags: (_result, _error, { advisorGroupId }) => [
        { type: 'HoldingsWOCusips', id: String(advisorGroupId) },
      ],
    }),
    getCusipDiversifications: builder.query<
      RecordResponse<AdvisorCusipDiversification>,
      { advisorGroupId: number | null; cusip: string }
    >({
      queryFn: async (
        { advisorGroupId, cusip },
        { getState },
        _extraOptions,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;
        const groupError = missingFirmGroupError(scope, advisorGroupId);
        if (groupError != null) {
          return { error: groupError };
        }

        const result = await baseQuery({
          url: classificationUrls.cusipDiversifications(
            scope,
            advisorGroupId,
            cusip,
          ),
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: result.data as RecordResponse<AdvisorCusipDiversification>,
        };
      },
      providesTags: (_result, _error, { advisorGroupId, cusip }) => [
        {
          type: 'CusipDiversifications',
          id: cusipDiversificationsTagId(advisorGroupId, cusip),
        },
      ],
    }),
    createCusipDiversification: builder.mutation<
      number,
      CusipSaveTagArgs & { body: AdvisorCusipDiversification }
    >({
      queryFn: async (
        { advisorGroupId, cusip, body },
        { getState },
        _extraOptions,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const result = await baseQuery({
          url: classificationUrls.cusipDiversifications(
            scope,
            advisorGroupId,
            cusip,
          ),
          method: 'POST',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as number };
      },
      invalidatesTags: (_result, _error, args) => cusipSaveTags(args),
    }),
    updateCusipDiversification: builder.mutation<
      boolean,
      CusipSaveTagArgs & {
        masterSecuritiesOverrideId: number;
        body: AdvisorCusipDiversification;
      }
    >({
      queryFn: async (
        { advisorGroupId, cusip, masterSecuritiesOverrideId, body },
        { getState },
        _extraOptions,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const result = await baseQuery({
          url: classificationUrls.cusipDiversification(
            scope,
            advisorGroupId,
            cusip,
            masterSecuritiesOverrideId,
          ),
          method: 'PUT',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as boolean };
      },
      invalidatesTags: (_result, _error, args) => cusipSaveTags(args),
    }),
    deleteCusipDiversification: builder.mutation<
      boolean,
      {
        advisorGroupId: number;
        cusip: string;
        masterSecuritiesOverrideId: number;
        body: AdvisorCusipDiversification;
      }
    >({
      queryFn: async (
        { advisorGroupId, cusip, masterSecuritiesOverrideId, body },
        { getState },
        _extraOptions,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const roleError = firmRoleForbiddenError(state);
        if (roleError != null) {
          return { error: roleError };
        }

        const encodedCusip = encodeCusipForUrl(cusip);
        const result = await baseQuery({
          url: advisorGroupUrl(
            advisorGroupId,
            `/cusips/${encodedCusip}/diversifications/${String(masterSecuritiesOverrideId)}`,
          ),
          method: 'DELETE',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as boolean };
      },
      invalidatesTags: (_result, _error, { advisorGroupId, cusip }) => [
        { type: 'Cusips', id: String(advisorGroupId) },
        { type: 'UnclassifiedCusips', id: String(advisorGroupId) },
        { type: 'HoldingsWOCusips', id: String(advisorGroupId) },
        {
          type: 'CusipDiversifications',
          id: cusipDiversificationsTagId(advisorGroupId, cusip),
        },
      ],
    }),
    bulkEditCusipDiversifications: builder.mutation<
      boolean,
      BulkEditCusipDiversificationRequest
    >({
      queryFn: async (body, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const result = await baseQuery({
          url: classificationUrls.bulkEditCusipDiversifications(scope),
          method: 'POST',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as boolean };
      },
      invalidatesTags: [
        { type: 'Cusips' },
        { type: 'UnclassifiedCusips' },
        { type: 'HoldingsWOCusips' },
        { type: 'CusipDiversifications' },
        { type: 'HoldingDiversifications' },
        { type: 'AdvisorHoldings' },
      ],
    }),
    getHoldingDiversifications: builder.query<
      HoldingDiversificationsResponse,
      { wealthHoldingId: number }
    >({
      queryFn: async (
        { wealthHoldingId },
        { getState },
        _extraOptions,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const result = await baseQuery({
          url: classificationUrls.holdingDiversifications(
            scope,
            wealthHoldingId,
          ),
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: result.data as HoldingDiversificationsResponse,
        };
      },
      providesTags: (_result, _error, { wealthHoldingId }) => [
        { type: 'HoldingDiversifications', id: String(wealthHoldingId) },
      ],
    }),
    saveHoldingDiversifications: builder.mutation<
      boolean,
      {
        wealthHoldingId: number;
        body: UpdateHoldingDiversificationRequest;
      }
    >({
      queryFn: async (
        { wealthHoldingId, body },
        { getState },
        _extraOptions,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const scopeResult = classificationScopeOrError(state);
        if ('error' in scopeResult) {
          return { error: scopeResult.error };
        }
        const { scope } = scopeResult;

        const result = await baseQuery({
          url: classificationUrls.holdingDiversifications(
            scope,
            wealthHoldingId,
          ),
          method: 'POST',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as boolean };
      },
      invalidatesTags: (_result, _error, { wealthHoldingId }) => [
        { type: 'HoldingDiversifications', id: String(wealthHoldingId) },
        { type: 'Cusips' },
        { type: 'UnclassifiedCusips' },
        { type: 'HoldingsWOCusips' },
        { type: 'CusipDiversifications' },
        { type: 'AdvisorHoldings' },
      ],
    }),
  }),
});

export const {
  useGetAdvisorHoldingsQuery,
  useExportAdvisorHoldingsMutation,
  useGetCusipsQuery,
  useExportCusipsMutation,
  useGetUnclassifiedCusipsQuery,
  useExportUnclassifiedCusipsMutation,
  useGetHoldingsWithoutCusipsQuery,
  useExportHoldingsWithoutCusipsMutation,
  useGetCusipDiversificationsQuery,
  useCreateCusipDiversificationMutation,
  useUpdateCusipDiversificationMutation,
  useDeleteCusipDiversificationMutation,
  useBulkEditCusipDiversificationsMutation,
  useGetHoldingDiversificationsQuery,
  useSaveHoldingDiversificationsMutation,
} = feedClassificationsApi;
